import { Controller } from "@hotwired/stimulus"

// Type-ahead for a single airport row in the city form. As the user types into
// the search field it queries /admin/airports/search; picking a result fills the
// IATA, name and coordinates, and (when the city has coordinates) estimates the
// drive time as a straight-line distance.
export default class extends Controller {
  static targets = ["search", "iata", "name", "driveMin", "coords", "origIata", "results"]
  static values = {
    url: String,
    cityLat: Number,
    cityLng: Number
  }

  connect() {
    this.onClickOutside = this.onClickOutside.bind(this)
    document.addEventListener("click", this.onClickOutside)
  }

  disconnect() {
    document.removeEventListener("click", this.onClickOutside)
    clearTimeout(this.timer)
  }

  input() {
    clearTimeout(this.timer)
    const q = this.searchTarget.value.trim()
    if (q.length < 2) return this.hide()
    this.timer = setTimeout(() => this.fetch(q), 200)
  }

  async fetch(q) {
    try {
      const res = await fetch(`${this.urlValue}?q=${encodeURIComponent(q)}`, {
        headers: { Accept: "application/json" }
      })
      if (!res.ok) return this.hide()
      this.render(await res.json())
    } catch {
      this.hide()
    }
  }

  render(airports) {
    if (!airports.length) return this.hide()
    this.resultsTarget.innerHTML = airports
      .map(
        (a) => `
        <button type="button" data-action="airport-autocomplete#pick"
          data-airport='${this.escape(JSON.stringify(a))}'
          class="block w-full text-left px-3 py-2 text-sm hover:bg-linen no-underline">
          <span class="font-medium text-charcoal">${this.escape(a.name)}</span>
          <span class="text-fg-muted">(${this.escape(a.iata)}) · ${this.escape(a.city || a.country)}</span>
        </button>`
      )
      .join("")
    this.resultsTarget.classList.remove("hidden")
  }

  pick(event) {
    const a = JSON.parse(event.currentTarget.dataset.airport)
    this.iataTarget.value = a.iata
    if (this.hasNameTarget) this.nameTarget.value = a.name
    this.coordsTarget.value = JSON.stringify({ lat: a.lat, lng: a.lng })
    // Coords belong to the chosen IATA — mark them current so they're preserved.
    if (this.hasOrigIataTarget) this.origIataTarget.value = a.iata
    this.searchTarget.value = `${a.name} (${a.iata})`
    this.estimateDriveTime(a)
    this.hide()
  }

  estimateDriveTime(a) {
    if (!this.hasDriveMinTarget) return
    if (this.driveMinTarget.value.trim() !== "") return // don't clobber a manual value
    if (!this.hasCityLatValue || !this.hasCityLngValue) return
    if (!this.cityLatValue && !this.cityLngValue) return

    const km = this.haversine(this.cityLatValue, this.cityLngValue, a.lat, a.lng)
    // Roads aren't straight: apply a detour factor and an average mixed speed,
    // then round to the nearest 5 minutes. Editable afterwards.
    const minutes = (km * 1.3) / 70 * 60
    this.driveMinTarget.value = Math.max(5, Math.round(minutes / 5) * 5)
  }

  haversine(lat1, lng1, lat2, lng2) {
    const toRad = (d) => (d * Math.PI) / 180
    const R = 6371
    const dLat = toRad(lat2 - lat1)
    const dLng = toRad(lng2 - lng1)
    const x =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2
    return 2 * R * Math.asin(Math.sqrt(x))
  }

  onClickOutside(event) {
    if (!this.element.contains(event.target)) this.hide()
  }

  hide() {
    this.resultsTarget.classList.add("hidden")
    this.resultsTarget.innerHTML = ""
  }

  escape(str) {
    const div = document.createElement("div")
    div.textContent = str == null ? "" : String(str)
    return div.innerHTML
  }
}
